import React, { useState, useCallback, useMemo, useEffect } from "react";
import { Shield, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@shadcn-in-nx/ui/button";
import { useBluelagoon } from "@bluelagoon";
import { analyzeFunctionAuth } from "./apiSecurityChecks";
import { liveProbeFunction } from "./apiSecurityLiveTest";
import { combineResults } from "./apiSecurityCombine";
import ApiSecurityRow from "./ApiSecurityRow";
import ApiSecurityPagination from "./ApiSecurityPagination";
import ApiSecurityScoreBar from "./ApiSecurityScoreBar";

const PAGE_SIZE = 25;

export default function ApiSecurityTester({ functions, baseUrl, onFixInChat }) {
    const { t } = useTranslation();
    const { call: fetchComponentCode } = useBluelagoon({
        method: "getComponentCode",
    });
    const [results, setResults] = useState({});
    const [isTesting, setIsTesting] = useState(false);
    const [page, setPage] = useState(0);

    const total = functions?.length || 0;
    const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const visibleFunctions = useMemo(
        () => (functions || []).slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE),
        [functions, page]
    );

    useEffect(() => {
        if (page > 0 && page >= pageCount) setPage(pageCount - 1);
    }, [page, pageCount]);

    const runOne = useCallback(
        async (func) => {
            setResults((p) => ({ ...p, [func.id]: { state: "running" } }));
            try {
                const codePromise = func.code
                    ? Promise.resolve(func.code)
                    : fetchComponentCode({ id: func.id }).then(
                          (r) => r?.data?.code || ""
                      );
                const livePromise = liveProbeFunction({
                    baseUrl,
                    functionName: func.functionName,
                });
                const [code, live] = await Promise.all([codePromise, livePromise]);
                const staticResult = analyzeFunctionAuth(code);
                const combined = combineResults({ staticResult, live, t });
                setResults((p) => ({
                    ...p,
                    [func.id]: { state: "done", ...combined, staticResult, live },
                }));
            } catch {
                setResults((p) => ({
                    ...p,
                    [func.id]: {
                        state: "done",
                        passed: false,
                        detail:
                            t("appPreview.detailTestFailed") ||
                            "Could not run test",
                    },
                }));
            }
        },
        [fetchComponentCode, baseUrl, t]
    );

    const runAll = useCallback(async () => {
        setIsTesting(true);
        try {
            await Promise.all(functions.map((f) => runOne(f)));
        } finally {
            setIsTesting(false);
        }
    }, [functions, runOne]);

    if (!functions || functions.length === 0) return null;

    const done = functions.filter((f) => results[f.id]?.state === "done");
    const passed = done.filter(
        (f) => results[f.id].passed && !results[f.id].warning
    ).length;
    const warned = done.filter(
        (f) => results[f.id].passed && results[f.id].warning
    ).length;
    const failed = done.filter((f) => !results[f.id].passed).length;

    return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-start justify-between gap-4 mb-4">
                <div>
                    <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                        <Shield className="w-5 h-5 text-blue-600" />
                        {t("appPreview.securityCheck") || "Security check"}
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                        {t("appPreview.securityCheckDescription") ||
                            "Verifies each API requires a logged-in user or service key."}
                    </p>
                </div>
                <Button onClick={runAll} disabled={isTesting} size="sm">
                    {isTesting ? (
                        <>
                            <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                            {t("appPreview.testing") || "Testing..."}
                        </>
                    ) : (
                        t("appPreview.testAll") || "Test all"
                    )}
                </Button>
            </div>

            {done.length > 0 && (
                <ApiSecurityScoreBar
                    passed={passed}
                    warned={warned}
                    failed={failed}
                    total={total}
                />
            )}

            <ul className="divide-y divide-gray-100 -mx-6 border-t border-gray-200">
                {visibleFunctions.map((func) => (
                    <ApiSecurityRow
                        key={func.id}
                        func={func}
                        result={results[func.id]}
                        onFixInChat={onFixInChat}
                    />
                ))}
            </ul>

            <ApiSecurityPagination
                page={page}
                pageCount={pageCount}
                total={total}
                pageSize={PAGE_SIZE}
                onChange={setPage}
            />
        </div>
    );
}
